import React, { useState, useRef } from 'react';
import { Receipt as ReceiptIcon, Search, Trash2, Eye } from 'lucide-react';
import { useData } from '../context/DataContext';
import { fmtRM2, fmtDate, genId } from '../lib/store';
import { PageHeader, EmptyState, Card } from '../components/Shared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import ShareDocActions from '../components/ShareDocActions';
import { SignatureBlock, DocLogo } from '../components/DocumentDialog';
import { BrandingOverrideSection, ReferenceNumberField } from '../components/BrandingFields';
import { toast } from 'sonner';

export default function Receipts() {
  const { receipts, customers, invoices, company, getCustomer, addItem, removeItem, recordPaymentOnInvoice, invoiceBalance, generateNumber, consumeNumber } = useData();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState(null);
  const printRef = useRef(null);

  const openNew = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ number: generateNumber('receipts', today), customerId: '', invoiceNumber: '', date: today, amount: 0, method: 'Bank Transfer' });
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.customerId || !form.amount) { toast.error('Customer and amount are required'); return; }
    const amount = Number(form.amount);
    addItem('receipts', { ...form, id: genId(), amount });
    consumeNumber('receipts', form.number);
    if (form.invoiceNumber) {
      recordPaymentOnInvoice(form.invoiceNumber, amount);
      toast.success(`Receipt issued — ${form.invoiceNumber} updated`);
    } else {
      toast.success('Receipt issued');
    }
    setOpen(false);
  };

  const onSelectInvoice = (number) => {
    const balance = invoiceBalance(number);
    setForm((f) => ({ ...f, invoiceNumber: number, amount: balance > 0 ? balance : f.amount }));
  };

  const filtered = receipts.filter((r) => (getCustomer(r.customerId)?.name || '').toLowerCase().includes(q.toLowerCase()) || (r.number || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Receipts" subtitle="Generate receipts and automatically update balances." actionLabel="Issue Receipt" onAction={openNew} />

      <Card className="mb-4 p-3">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search receipts..." className="pl-9" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <EmptyState icon={ReceiptIcon} title="No receipts" subtitle="Issue a receipt for a payment." />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Number</TableHead>
                <TableHead className="font-semibold text-slate-600">Customer</TableHead>
                <TableHead className="font-semibold text-slate-600">For Invoice</TableHead>
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Method</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Amount</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50">
                  <TableCell className="font-semibold text-pink-600">{r.number || '—'}</TableCell>
                  <TableCell className="text-slate-700">{getCustomer(r.customerId)?.name || '—'}</TableCell>
                  <TableCell className="text-slate-500">{r.invoiceNumber || '—'}</TableCell>
                  <TableCell className="text-slate-500">{fmtDate(r.date)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-slate-600">{r.method}</Badge></TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">{fmtRM2(r.amount)}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => setPreview(r)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Eye className="h-4 w-4" /></button>
                    <button onClick={() => { removeItem('receipts', r.id); toast.success('Deleted'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-lg">
          <DialogHeader><DialogTitle>Issue Receipt</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <ReferenceNumberField value={form.number} onChange={(v) => setField('number', v)} testid="receipt-number-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Customer</Label>
                <Select value={form.customerId} onValueChange={(v) => setForm({ ...form, customerId: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>For Invoice (optional)</Label>
                <Select value={form.invoiceNumber} onValueChange={onSelectInvoice}>
                  <SelectTrigger className="mt-1.5" data-testid="receipt-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
                  <SelectContent>{invoices.map((i) => <SelectItem key={i.id} value={i.number}>{i.number}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Amount (RM)</Label><Input type="number" className="mt-1.5" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="sm:col-span-2">
                <Label>Payment Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{['Bank Transfer', 'Cash', 'Cheque', 'Online Banking', 'Credit Card'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <BrandingOverrideSection form={form} setField={setField} company={company} testid="receipt-branding-section" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save}>Issue Receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-md p-0">
          <DialogHeader className="sr-only"><DialogTitle>Receipt {preview?.number || ''}</DialogTitle></DialogHeader>
          {preview && (
            <div>
            <div className="bg-white p-8 text-center" ref={printRef} data-testid="receipt-print-area">
              <div className="mb-3 flex justify-center"><DocLogo doc={preview} company={company} accentBg="bg-pink-600" /></div>
              <p className="text-xs uppercase tracking-widest text-slate-400">Official Receipt</p>
              <h2 className="text-xl font-bold text-slate-900">{preview.number || '—'}</h2>
              <p className="mt-1 text-sm text-slate-500">{company.name}</p>
              <div className="my-5 rounded-xl bg-emerald-50 py-4"><p className="text-xs text-emerald-700">Amount Received</p><p className="text-3xl font-extrabold text-emerald-600">{fmtRM2(preview.amount)}</p></div>
              <div className="space-y-1 text-left text-sm text-slate-600">
                <div className="flex justify-between"><span className="text-slate-400">Received From</span><span className="font-medium">{getCustomer(preview.customerId)?.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Date</span><span>{fmtDate(preview.date)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Method</span><span>{preview.method}</span></div>
                {preview.invoiceNumber && <div className="flex justify-between"><span className="text-slate-400">Invoice</span><span>{preview.invoiceNumber}</span></div>}
              </div>
              <div className="flex justify-center"><SignatureBlock doc={preview} company={company} align="center" /></div>
            </div>
              <div className="flex justify-center border-t border-slate-100 bg-slate-50 py-3">
                <ShareDocActions
                  targetRef={printRef}
                  docLabel="Receipt"
                  number={preview.number}
                  recipientName={getCustomer(preview.customerId)?.contact || getCustomer(preview.customerId)?.name}
                  recipientEmail={getCustomer(preview.customerId)?.email}
                  recipientPhone={getCustomer(preview.customerId)?.phone}
                  amountText={fmtRM2(preview.amount)}
                  companyName={company.name}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
